import { Fragment } from 'react';

/**
 * Título de seção vindo do CMS, com quebra de linha e palavras marcadas.
 *
 * O texto carrega três marcas, e só três: quebra de linha vira `<br>`,
 * `*assim*` vira o destaque da seção, e `_assim_` vira o texto vazado — que só
 * existe no banner. É a menor convenção que cobre os seis títulos do site sem
 * abrir a porta para o cliente colar HTML dentro do conteúdo.
 *
 * O texto nunca é interpretado como marcação: cada pedaço vira nó de texto do
 * React, então um `<script>` digitado no painel aparece escrito na tela, que é
 * o comportamento certo.
 *
 * Marca sem par fica como texto. Quem escreveu não quis marcar, ou errou, e nos
 * dois casos sumir com o caractere seria pior do que mostrá-lo.
 */
export function TituloComDestaque({
  texto,
  className,
  destaqueClassName,
  vazadoClassName,
  id,
  como: Elemento = 'h2',
}: {
  texto: string;
  className?: string;
  /** Como o `*destaque*` se pinta — muda entre fundo claro e fundo escuro. */
  destaqueClassName: string;
  /** Como o `_vazado_` se pinta. Sem isto, `_assim_` fica texto comum. */
  vazadoClassName?: string;
  id?: string;
  /** `h1` no banner, `h2` nas seções — a página só pode ter um `h1`. */
  como?: 'h1' | 'h2';
}) {
  return (
    <Elemento id={id} className={className}>
      {texto.split('\n').map((linha, indiceDaLinha) => (
        <Fragment key={`${String(indiceDaLinha)}-${linha}`}>
          {indiceDaLinha > 0 && <br />}
          {partir(linha, vazadoClassName !== undefined).map((pedaco, indice) => {
            if (pedaco.marca === 'destaque') {
              return (
                <em key={indice} className={destaqueClassName}>
                  {pedaco.texto}
                </em>
              );
            }

            if (pedaco.marca === 'vazado') {
              return (
                <span key={indice} className={vazadoClassName}>
                  {pedaco.texto}
                </span>
              );
            }

            return <Fragment key={indice}>{pedaco.texto}</Fragment>;
          })}
        </Fragment>
      ))}
    </Elemento>
  );
}

export type Marca = 'nenhuma' | 'destaque' | 'vazado';

interface Pedaco {
  readonly texto: string;
  readonly marca: Marca;
}

const MARCAS: readonly { readonly caractere: string; readonly marca: Marca }[] = [
  { caractere: '*', marca: 'destaque' },
  { caractere: '_', marca: 'vazado' },
];

/** Reparte a linha nos pares de marca, da esquerda para a direita. */
export function partir(linha: string, comVazado = true): Pedaco[] {
  const ativas = MARCAS.filter((m) => comVazado || m.marca !== 'vazado');
  const pedacos: Pedaco[] = [];
  let resto = linha;

  while (resto.length > 0) {
    // Vale a marca que aparecer primeiro, e só se ela tiver par depois.
    let escolhida: { abre: number; fecha: number; marca: Marca } | null = null;

    for (const { caractere, marca } of ativas) {
      const abre = resto.indexOf(caractere);

      if (abre === -1) continue;

      const fecha = resto.indexOf(caractere, abre + 1);

      if (fecha === -1) continue;
      if (escolhida === null || abre < escolhida.abre) escolhida = { abre, fecha, marca };
    }

    if (escolhida === null) {
      pedacos.push({ texto: resto, marca: 'nenhuma' });
      break;
    }

    if (escolhida.abre > 0) {
      pedacos.push({ texto: resto.slice(0, escolhida.abre), marca: 'nenhuma' });
    }

    pedacos.push({
      texto: resto.slice(escolhida.abre + 1, escolhida.fecha),
      marca: escolhida.marca,
    });

    resto = resto.slice(escolhida.fecha + 1);
  }

  return pedacos;
}
